import { BillingInfo } from '@citrite/sf-api';
import {
	euroVatCountryCodes,
	gstCountries,
	pstCanadianProvinces,
} from './addressFieldConstants';

export enum addressFieldId {
	addressLine1 = 'addressLine1',
	city = 'city',
	country = 'country',
	australianBusinessNumber = 'australianBusinessNumber',
	europeanVATId = 'europeanVATId',
	gstHSTNumber = 'gstHSTNumber',
	postalCode = 'postalCode',
	province = 'province',
	provincialSalesTax = 'provincialSalesTax',
	quebecSalesTax = 'quebecSalesTax',
	state = 'state',
	zipCode = 'zipCode',
}

export type AddressField = {
	id: addressFieldId;
	localizationKey: string;
	colWidth: number;
	dependsOn: addressFieldId[];
};

export enum addressFieldStatus {
	hidden = 0,
	required = 1,
	optional = 2,
}

export const addressLineOne: AddressField = {
	id: addressFieldId.addressLine1,
	localizationKey: 'billing_address.street',
	dependsOn: [],
	colWidth: 24,
};

export const cityField: AddressField = {
	id: addressFieldId.city,
	localizationKey: 'billing_address.city',
	dependsOn: [addressFieldId.country],
	colWidth: 24,
};

export const narrowCityField: AddressField = {
	id: addressFieldId.city,
	localizationKey: 'billing_address.city',
	dependsOn: [addressFieldId.country],
	colWidth: 12,
};

export const countryField: AddressField = {
	id: addressFieldId.country,
	localizationKey: 'billing_address.country',
	dependsOn: [],
	colWidth: 12,
};

export const australianBusinessNumberField: AddressField = {
	id: addressFieldId.australianBusinessNumber,
	localizationKey: 'billing_address.abn_number',
	dependsOn: [addressFieldId.country],
	colWidth: 24,
};

export const europeanVatField: AddressField = {
	id: addressFieldId.europeanVATId,
	localizationKey: 'billing_address.european_vat_id',
	dependsOn: [addressFieldId.country],
	colWidth: 24,
};

export const gstHSTField: AddressField = {
	id: addressFieldId.gstHSTNumber,
	localizationKey: 'billing_address.gst_hst_number',
	dependsOn: [addressFieldId.country, addressFieldId.province],
	colWidth: 12,
};

export const postalCodeField: AddressField = {
	id: addressFieldId.postalCode,
	localizationKey: 'billing_address.postal_code',
	dependsOn: [addressFieldId.country],
	colWidth: 12,
};

export const provinceField: AddressField = {
	id: addressFieldId.province,
	localizationKey: 'billing_address.province',
	dependsOn: [addressFieldId.country],
	colWidth: 12,
};

export const provincialSalesTaxField: AddressField = {
	id: addressFieldId.provincialSalesTax,
	localizationKey: 'billing_address.pst_number',
	dependsOn: [addressFieldId.country, addressFieldId.province],
	colWidth: 12,
};
export const quebecSalesTaxField: AddressField = {
	id: addressFieldId.quebecSalesTax,
	localizationKey: 'billing_address.qst_number',
	dependsOn: [addressFieldId.country, addressFieldId.province],
	colWidth: 12,
};

export const stateField: AddressField = {
	id: addressFieldId.state,
	localizationKey: 'billing_address.state',
	dependsOn: [addressFieldId.country],
	colWidth: 12,
};

export const zipCodeField: AddressField = {
	id: addressFieldId.zipCode,
	localizationKey: 'billing_address.zip',
	dependsOn: [addressFieldId.country],
	colWidth: 12,
};

export function getAddressFieldStatus(
	addressField: AddressField,
	dependencyValues: string[]
): addressFieldStatus {
	switch (addressField.id) {
		case addressFieldId.addressLine1:
			return addressFieldStatus.required;
		case addressFieldId.city:
			return addressFieldStatus.required;
		case addressFieldId.country:
			return addressFieldStatus.required;
		case addressFieldId.europeanVATId:
			const isVatCountryCode =
				euroVatCountryCodes.filter(x => dependencyValues.includes(x)).length > 0;
			return isVatCountryCode ? addressFieldStatus.optional : addressFieldStatus.hidden;
		// Show Australian Business Number field if the country code is Australia
		case addressFieldId.australianBusinessNumber:
			if (dependencyValues.includes('AU')) {
				return addressFieldStatus.optional;
			}
			return addressFieldStatus.hidden;
		//If the country code is Canada, check the province, otherwise check the country code
		case addressFieldId.gstHSTNumber:
			const isGSTApplicable =
				gstCountries.filter(x => dependencyValues.includes(x)).length > 0;
			return isGSTApplicable ? addressFieldStatus.optional : addressFieldStatus.hidden;
		// If the country code is U.S. zip is used, otherwise it's optional
		case addressFieldId.postalCode:
			if (dependencyValues.includes('US')) {
				return addressFieldStatus.hidden;
			}
			return addressFieldStatus.optional;
		// If the country code is Canada, we require province, otherwise it's hidden
		case addressFieldId.province:
			if (dependencyValues.includes('CA')) {
				return addressFieldStatus.required;
			}
			return addressFieldStatus.hidden;
		// If the country code is Canada, we check if PST is applicable based on the province code
		case addressFieldId.provincialSalesTax:
			const isPSTApplicable =
				dependencyValues.includes('CA') &&
				pstCanadianProvinces.filter(x => dependencyValues.includes(x)).length > 0;
			return isPSTApplicable ? addressFieldStatus.optional : addressFieldStatus.hidden;
		// Check if the country code is Canada and the province code is Quebec
		case addressFieldId.quebecSalesTax:
			if (dependencyValues.includes('CA') && dependencyValues.includes('QC')) {
				return addressFieldStatus.optional;
			}
			return addressFieldStatus.hidden;
		// If the country code is US, we require state, otherwise it's hidden
		case addressFieldId.state:
			if (dependencyValues.includes('US')) {
				return addressFieldStatus.required;
			}
			return addressFieldStatus.hidden;
		// If the country code is U.S. we require zip, otherwise it's hidden
		case addressFieldId.zipCode:
			if (dependencyValues.includes('US')) {
				return addressFieldStatus.required;
			}
			return addressFieldStatus.hidden;
	}
	// If we don't know what the field is, let's not show it
	return addressFieldStatus.hidden;
}

export function getRequiredFields(
	addressField: AddressField,
	BillingAddress: BillingInfo
) {
	const dependencyValues: string[] = [];
	addressField.dependsOn.forEach(dependency => {
		switch (dependency) {
			case addressFieldId.country:
				if (BillingAddress.Country) {
					dependencyValues.push(BillingAddress.Country);
				}
				break;
			case addressFieldId.province:
			case addressFieldId.state:
				if (BillingAddress.State) {
					dependencyValues.push(BillingAddress.State);
				}
				break;
			case addressFieldId.postalCode:
				if (BillingAddress.Zip) {
					dependencyValues.push(BillingAddress.Zip);
				}
		}
	});
	return dependencyValues;
}

export const primaryTaxFields: AddressField[] = [
	australianBusinessNumberField,
	europeanVatField,
	gstHSTField,
	provincialSalesTaxField,
];

export const additionalTaxFields: AddressField[] = [quebecSalesTaxField];
